import React, { Component } from 'react'
import Card from '@material-ui/core/Card'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TablePagination from '@material-ui/core/TablePagination'
import ExpandMore from '@material-ui/icons/ExpandMore'
import ChevronRight from '@material-ui/icons/ChevronRight'

import surveyApiService from '../../providers/survey/surveyApiService'
import { userName, formatDate, Stars } from './surveyHelpers'
import SurveyStatusList from './SurveyStatusList'

import './SurveyRatingsStyles.css'

const auctionTitle = (auction) => {
  if (!auction) return 'No auction'
  return auction.title || (auction.searchNumber ? `Auction ${auction.searchNumber}` : 'Auction')
}

/**
 * Admin list of every submitted rating, grouped by auction. Opening an auction
 * shows its lots' ratings (who it came from, who was rated); opening a lot row
 * shows the per-question breakdown.
 */
class SurveyRatingsList extends Component {
  constructor (props) {
    super(props)
    this.state = {
      groups: [],
      totalCount: 0,
      page: 0,
      limit: 25,
      loaded: false,
      expandedAuctions: {},
      expanded: {}
    }
    this.handleChangePage = this.handleChangePage.bind(this)
    this.handleChangeLimit = this.handleChangeLimit.bind(this)
  }

  componentDidMount () {
    this.load()
  }

  async load () {
    const { page, limit } = this.state
    const res = await surveyApiService.getSurveysByAuction({ page: page + 1, limit })
    this.setState({
      groups: res.auctions,
      totalCount: res.totalCount,
      loaded: true
    })
  }

  handleChangePage (event, page) {
    this.setState({ page }, () => this.load())
  }

  handleChangeLimit (event) {
    this.setState({ limit: parseInt(event.target.value, 10), page: 0 }, () => this.load())
  }

  toggleAuction (key) {
    this.setState(prev => ({
      expandedAuctions: { ...prev.expandedAuctions, [key]: !prev.expandedAuctions[key] }
    }))
  }

  toggleExpand (id) {
    this.setState(prev => ({
      expanded: { ...prev.expanded, [id]: !prev.expanded[id] }
    }))
  }

  renderAnswers (survey) {
    if (!this.state.expanded[survey._id]) return null
    return (
      <TableRow key={`${survey._id}-answers`} className='answers-row'>
        <TableCell colSpan={7}>
          <div className='answers-wrap'>
            {(survey.answers || []).map((answer, index) => (
              <div className='answer-line' key={index}>
                <span className='answer-title'>
                  {answer.title}
                  {Number(answer.weight) > 0 &&
                    <span className='answer-weight'>{answer.weight}%</span>}
                </span>
                <Stars value={answer.rating} />
              </div>
            ))}
          </div>
        </TableCell>
      </TableRow>
    )
  }

  renderSurvey (survey) {
    const lot = survey.lot || {}
    const lotTitle = (lot.publicDetails && lot.publicDetails.title) || '-'
    return [
      <TableRow
        key={survey._id}
        className='rating-row'
        onClick={() => this.toggleExpand(survey._id)}
      >
        <TableCell className='lot-id'>{lot.searchNumericId || '-'}</TableCell>
        <TableCell>{lotTitle}</TableCell>
        <TableCell><Stars value={survey.rating} /></TableCell>
        <TableCell>{userName(survey.respondent)}</TableCell>
        <TableCell>{userName(survey.subject)}</TableCell>
        <TableCell className='role-cell'>
          {survey.role === 'seller' ? 'Seller → Buyer' : 'Buyer → Assessor'}
        </TableCell>
        <TableCell>{formatDate(survey.completedAt || survey.createdAt)}</TableCell>
      </TableRow>,
      this.renderAnswers(survey)
    ]
  }

  renderGroup (group) {
    const { auction } = group
    const key = auction ? auction._id : 'none'
    const open = !!this.state.expandedAuctions[key]
    const lotCount = new Set(group.surveys.map(survey => String(survey.lot && survey.lot._id))).size

    return [
      <TableRow
        key={`auction-${key}`}
        className={open ? 'auction-row open' : 'auction-row'}
        onClick={() => this.toggleAuction(key)}
      >
        <TableCell colSpan={2}>
          {open ? <ExpandMore className='auction-chevron' /> : <ChevronRight className='auction-chevron' />}
          <span className='survey-auction-title'>{auctionTitle(auction)}</span>
          {auction && auction.liveAt &&
            <span className='survey-auction-date'>{formatDate(auction.liveAt)}</span>}
        </TableCell>
        <TableCell><Stars value={group.rating} /></TableCell>
        <TableCell colSpan={3} className='auction-meta'>
          {lotCount} {lotCount === 1 ? 'lot' : 'lots'} · {group.count} {group.count === 1 ? 'rating' : 'ratings'}
        </TableCell>
        <TableCell>{formatDate(group.lastRatedAt)}</TableCell>
      </TableRow>,
      open && group.surveys.map(survey => this.renderSurvey(survey))
    ]
  }

  render () {
    const { groups, totalCount, page, limit, loaded } = this.state

    return (
      <div>
        <p className='page-title'>Survey Ratings</p>
        <Card className='survey-ratings-list'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lot ID</TableCell>
                <TableCell>Lot Name</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>From User</TableCell>
                <TableCell>Rated User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loaded && groups.length === 0 &&
                <TableRow>
                  <TableCell colSpan={7}>No ratings submitted yet.</TableCell>
                </TableRow>}

              {groups.map(group => this.renderGroup(group))}
            </TableBody>
          </Table>

          <TablePagination
            component='div'
            count={totalCount}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeLimit}
          />
        </Card>

        <SurveyStatusList status='awaiting' />
        <SurveyStatusList status='completed' />
      </div>
    )
  }
}

export default SurveyRatingsList
